import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rulesApi } from '../services/api';
import { getCategoryIconUrl } from '../utils/images';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import './ClubGuydom.css';

function ClubGuydom() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [voting, setVoting] = useState({}); // Track which submission is being voted on
  const [userVotes, setUserVotes] = useState({}); // Track which submissions user has voted on { submitId: vote }
  const [changeVoteMode, setChangeVoteMode] = useState({}); // Track which submissions are in "change vote" mode { submitId: true/false }
  
  // Submit New Rule form state
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitFormData, setSubmitFormData] = useState({
    cat_id: '',
    title: '',
    rule: ''
  });

  useEffect(() => {
    if (!authLoading && (!user || (user.profile !== 'admin' && user.profile !== 'club'))) {
      navigate('/profile');
      return;
    }
    if (user) {
      loadSubmissions();
      loadCategories();
    }
  }, [user, authLoading, navigate]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await rulesApi.getClubSubmissions();
      
      let loadedSubmissions = [];
      if (response.data) {
        if (response.data.success && response.data.data) {
          loadedSubmissions = Array.isArray(response.data.data) ? response.data.data : [];
        } else if (Array.isArray(response.data)) {
          loadedSubmissions = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          loadedSubmissions = response.data.data;
        }
      }
      
      // Build userVotes map from submissions that have user_vote property
      const votesMap = {};
      loadedSubmissions.forEach(sub => {
        const submitId = sub.submit_id || sub.id;
        if (submitId && (sub.user_vote !== undefined || sub.has_voted)) {
          votesMap[submitId] = sub.user_vote !== undefined ? sub.user_vote : (sub.vote || 0);
        }
      });
      setUserVotes(votesMap);
      
      // Sort: submissions user hasn't voted on first, then voted ones at bottom
      const sortedSubmissions = [...loadedSubmissions].sort((a, b) => {
        const aId = a.submit_id || a.id;
        const bId = b.submit_id || b.id;
        const aHasVoted = votesMap[aId] !== undefined || a.has_voted === true;
        const bHasVoted = votesMap[bId] !== undefined || b.has_voted === true;
        
        // If both have same vote status, maintain original order (by submit_id desc)
        if (aHasVoted === bHasVoted) {
          return (b.submit_id || b.id || 0) - (a.submit_id || a.id || 0);
        }
        
        // Not voted (false) comes before voted (true)
        return aHasVoted ? 1 : -1;
      });
      
      setSubmissions(sortedSubmissions);
      
      // Reset change vote mode when loading new submissions
      setChangeVoteMode({});
    } catch (err) {
      console.error('Error loading club submissions:', err);
      setError(err.response?.data?.message || 'Failed to load submissions');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (submitId, vote) => {
    if (voting[submitId]) return; // Prevent double voting
    
    try {
      setVoting(prev => ({ ...prev, [submitId]: true }));
      setError('');
      
      await rulesApi.submitClubVote(submitId, vote);
      
      // Update userVotes immediately (optimistic update)
      setUserVotes(prev => ({ ...prev, [submitId]: vote }));
      
      // Exit change vote mode after voting
      setChangeVoteMode(prev => ({ ...prev, [submitId]: false }));
      
      // Reload submissions to update UI (in case backend returns vote status)
      loadSubmissions();
    } catch (err) {
      console.error('Error submitting vote:', err);
      setError(err.response?.data?.message || 'Failed to submit vote');
    } finally {
      setVoting(prev => ({ ...prev, [submitId]: false }));
    }
  };

  const handleChangeVoteClick = (submitId) => {
    // Show yes/no buttons for this submission
    setChangeVoteMode(prev => ({ ...prev, [submitId]: true }));
  };

  const loadCategories = async () => {
    try {
      const response = await rulesApi.getCategories();
      let loadedCategories = [];
      if (Array.isArray(response.data)) {
        loadedCategories = response.data;
      } else if (response.data && response.data.data) {
        loadedCategories = response.data.data;
      }
      setCategories(loadedCategories);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const handleSubmitFormChange = (e) => {
    const { name, value } = e.target;
    setSubmitFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitNewRule = async (e) => {
    e.preventDefault();
    
    if (!submitFormData.cat_id || !submitFormData.title || !submitFormData.rule.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      // API expects `gr_cat_id`, so map the selected category id
      await rulesApi.submitNewRule({
        gr_cat_id: submitFormData.cat_id,
        title: submitFormData.title,
        rule: submitFormData.rule
      });
      
      // Reset form
      setSubmitFormData({
        cat_id: '',
        title: '',
        rule: ''
      });
      setShowSubmitForm(false);
      
      // Show success message (you could add a success state if needed)
      alert('Rule submitted successfully!');
    } catch (err) {
      console.error('Error submitting rule:', err);
      setError(err.response?.data?.message || 'Failed to submit rule');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !user) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading />
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="club-guydom-page">
          <h1>Club Guydom</h1>
          
          {/* Submit New Rule Section */}
          <div className="submit-new-rule-section">
            <h2>Submit New Rule</h2>
            {!showSubmitForm ? (
              <button 
                onClick={() => setShowSubmitForm(true)}
                className="btn btn-primary show-form-btn"
              >
                <i className="bi bi-plus-circle"></i> Submit New Rule
              </button>
            ) : (
              <form onSubmit={handleSubmitNewRule} className="submit-rule-form">
                <div className="form-group">
                  <label htmlFor="cat_id">Category *</label>
                  <select
                    id="cat_id"
                    name="cat_id"
                    value={submitFormData.cat_id}
                    onChange={handleSubmitFormChange}
                    required
                    disabled={submitting}
                    className="form-control"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat.cat_id || cat.id} value={cat.cat_id || cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {submitFormData.cat_id && (() => {
                    const selectedCat = categories.find(c => (c.cat_id || c.id) === submitFormData.cat_id);
                    return selectedCat?.icon ? (
                      <div className="category-icon-preview">
                        <img 
                          src={getCategoryIconUrl(selectedCat.icon)}
                          alt={selectedCat.name}
                          className="category-icon"
                        />
                        <span>{selectedCat.name}</span>
                      </div>
                    ) : null;
                  })()}
                </div>

                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={submitFormData.title}
                    onChange={handleSubmitFormChange}
                    required
                    disabled={submitting}
                    maxLength={255}
                    className="form-control"
                    placeholder="Enter rule title"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="rule">Rule *</label>
                  <textarea
                    id="rule"
                    name="rule"
                    value={submitFormData.rule}
                    onChange={handleSubmitFormChange}
                    required
                    disabled={submitting}
                    rows={6}
                    className="form-control"
                    placeholder="Enter the rule content"
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Rule'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowSubmitForm(false);
                      setSubmitFormData({ cat_id: '', title: '', rule: '' });
                      setError('');
                    }}
                    className="btn btn-secondary"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          <h2 className="voting-section-title">Recently Submitted Rules- We need your vote!</h2>
          
          {error && <div className="error-message">{error}</div>}
          
          {loading ? (
            <Loading />
          ) : submissions.length === 0 ? (
            <p className="no-submissions">No submissions found with status "vote1"</p>
          ) : (
            <div className="submissions-grid">
              {submissions.map((submission) => (
                <div key={submission.submit_id || submission.id} className="submission-card">
                  <div className="submission-header">
                    <h3 className="submission-title">{submission.title || 'No Title'}</h3>
                  </div>
                  
                  <div className="submission-body">
                    <div className="submission-field">
                      <strong>Name:</strong> {submission.name || 'N/A'}
                    </div>
                    
                    <div className="submission-field">
                      <strong>Email:</strong> {submission.email || 'N/A'}
                    </div>
                    
                    <div className="submission-field">
                      <strong>Rule:</strong>
                      <div className="submission-rule">{submission.rule || 'N/A'}</div>
                    </div>
                  </div>
                  
                  <div className="submission-actions">
                    {(() => {
                      const submitId = submission.submit_id || submission.id;
                      const hasVoted = userVotes[submitId] !== undefined;
                      const inChangeMode = changeVoteMode[submitId];
                      const isVoting = voting[submitId];
                      
                      // Show "Change My Vote" button if user has voted and not in change mode
                      if (hasVoted && !inChangeMode) {
                        return (
                          <button
                            onClick={() => handleChangeVoteClick(submitId)}
                            className="vote-btn change-vote"
                            title="Change My Vote"
                          >
                            <i className="bi bi-arrow-repeat"></i>
                            Change My Vote
                          </button>
                        );
                      }
                      
                      // Show yes/no buttons if user hasn't voted OR is in change mode
                      return (
                        <>
                          <button
                            onClick={() => handleVote(submitId, 1)}
                            disabled={isVoting}
                            className="vote-btn vote-up"
                            title="Vote Yes"
                          >
                            <i className="bi bi-hand-thumbs-up-fill"></i>
                            {isVoting ? 'Voting...' : 'Yes'}
                          </button>
                          
                          <button
                            onClick={() => handleVote(submitId, 0)}
                            disabled={isVoting}
                            className="vote-btn vote-down"
                            title="Vote No"
                          >
                            <i className="bi bi-hand-thumbs-down-fill"></i>
                            {isVoting ? 'Voting...' : 'No'}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default ClubGuydom;
